import { WebPageEvents } from '@griply/types';
import Typography from '@mui/material/Typography';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Repeat, Target, TodoList } from 'shared/assets/icons';
import { Button, Sizes } from 'shared/components/ui/button';
import { Icon } from 'shared/components/ui/icon';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';

import { OnboardingHeader } from '../onboarding-header';
import { ProgressBar } from '../progress-bar';
import * as Styled from './welcome.style';

const WELCOME_URL = '/images/onboarding/welcome.png';

export type WelcomeProps = {
  nextStep: () => void;
  prevStep: () => void;
  currentStep: number;
  previousStep: number;
  totalSteps: number;
};

export const Welcome: React.FC<WelcomeProps> = ({
  nextStep,
  prevStep,
  currentStep,
  previousStep,
  totalSteps,
}) => {
  useTrackPageVisitEvent(WebPageEvents.OnboardingWelcome, undefined);
  const { t } = useTranslation();

  const usps = useMemo(
    () => [
      {
        icon: Target,
        title: t('forms.onboarding.welcome.usps.1.title'),
        description: t('forms.onboarding.welcome.usps.1.description'),
      },
      {
        icon: TodoList,
        title: t('forms.onboarding.welcome.usps.2.title'),
        description: t('forms.onboarding.welcome.usps.2.description'),
      },
      {
        icon: Repeat,
        title: t('forms.onboarding.welcome.usps.3.title'),
        description: t('forms.onboarding.welcome.usps.3.description'),
      },
    ],
    [t],
  );

  return (
    <Styled.OnboardingContainer>
      <Styled.LeftPanel>
        <OnboardingHeader onBack={prevStep} />

        <Styled.LeftPanelContent>
          <ProgressBar
            current={currentStep}
            previous={previousStep}
            total={totalSteps}
          />

          <Styled.Body id="life-areas-form">
            <Typography variant="h4" component="h1">
              {t('forms.onboarding.welcome.title')}
            </Typography>
            <Styled.Subtitle>
              {t('forms.onboarding.welcome.subtitle')}
            </Styled.Subtitle>

            <Styled.List>
              {usps.map(({ icon, title, description }) => (
                <Styled.ListItem key={title}>
                  <Styled.ListItemIconContainer>
                    <Icon icon={icon} />
                  </Styled.ListItemIconContainer>
                  <Styled.ListItemContent>
                    <Styled.ListItemTitle>{title}</Styled.ListItemTitle>
                    <Typography variant="overline">{description}</Typography>
                  </Styled.ListItemContent>
                </Styled.ListItem>
              ))}
            </Styled.List>
          </Styled.Body>

          <Styled.FooterArea>
            <Button
              form="life-areas-form"
              onClick={nextStep}
              size={Sizes.Medium}
              fullWidth
              alignCenter
            >
              {t('forms.onboarding.continue.label')}
            </Button>
          </Styled.FooterArea>
        </Styled.LeftPanelContent>
      </Styled.LeftPanel>
      <Styled.RightPanelWrapper>
        <Styled.Illustration src={WELCOME_URL} />
      </Styled.RightPanelWrapper>
    </Styled.OnboardingContainer>
  );
};
